import { FormInstance } from "antd"

// Shared formatting + replay-link helpers for the data-delivery domain.
// Extracted from delivery-composer / delivery-detail to remove duplication.

export function fmtBytes(s: string): string {
  const n = Number(s)
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`

  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

// Humanized duration. Delivery hours is the primary tracking unit, so durations
// can span hours — show h/m for long spans, m/s otherwise.
export function fmtDuration(totalSec: number): string {
  if (!totalSec) return "0s"
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = Math.floor(totalSec % 60)
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`

  return `${s}s`
}

// Decimal hours alongside the humanized form, for headline stats.
export function fmtHours(totalSec: number): string {
  return `${(totalSec / 3600).toFixed(2)} h (${fmtDuration(totalSec)})`
}

// Event-replay player (Vercel `buff-event-replay-player`). Reads `?env=<id>&v=<recordingId|s3Prefix>`
// — the recording id is the tail of the source s3 prefix, so it resolves the recording on its own.
// Recordings in this pool are sourced from the dev index/S3, so the player must look in dev.
export const REPLAY_BASE = "https://event-replay.app.buff.game"
export const REPLAY_ENV = "dev"

export function replayUrl(v: string): string {
  return `${REPLAY_BASE}/?env=${REPLAY_ENV}&v=${encodeURIComponent(v)}`
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface ParsedRecordingIds {
  ids: string[]
  invalid: string[]
}

export function parseRecordingIds(text: string): ParsedRecordingIds {
  const tokens = text.split(/[\s,;]+/).map(t => t.trim().toLowerCase()).filter(Boolean)
  const ids = new Set<string>()
  const invalid = new Set<string>()
  for (const token of tokens) {
    (UUID_RE.test(token) ? ids : invalid).add(token)
  }

  return { ids: Array.from(ids), invalid: Array.from(invalid) }
}

const MAX_INVALID_SHOWN = 10

export function invalidIdsMessage(invalid: string[]): string {
  const shown = invalid.slice(0, MAX_INVALID_SHOWN).join(", ")
  const more = invalid.length - MAX_INVALID_SHOWN

  return `Not recording ids, skipped: ${shown}${more > 0 ? ` and ${more} more` : ""}`
}

export function fmtMinutes(ms: number): string {
  return `${(ms / 60000).toFixed(1)} min`
}

export function fmtClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mmss = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`

  return h > 0 ? `${h}:${mmss}` : mmss
}

export function seedForm<T extends object>(form: FormInstance<T>, values: T): void {
  form.resetFields()
  form.setFields(Object.entries(values).map(([name, value]) => ({ name, value, touched: false })))
}
